/**
 * نبض بازار — Market Intelligence
 * =================================
 * یک پرسش را جواب می‌دهد: «الان وضعیت کلی بازار کریپتو چیست و چه چیزی
 * ممکن است حرکتش بدهد؟»
 *
 * چهار تصمیم که عمدی‌اند:
 *
 * ۱. **همه‌چیز از API خود ارزینوست.** مرورگر به هیچ منبع بیرونی وصل
 *    نمی‌شود؛ داده در بک‌اند نرمال و ذخیره شده است.
 * ۲. **رژیم بازار همیشه با دلایلش می‌آید.** ادعای «ریسک‌پذیری» بدون گفتنِ
 *    چرا، جعبه‌سیاه است؛ هر رأی با جهت و عددش نمایش داده می‌شود.
 * ۳. **عددِ نامشخص، «—» است نه صفر.** صفرِ دروغ از نبودِ داده بدتر است.
 * ۴. **هیچ چیزِ این صفحه سیگنال خرید/فروش نیست** و همین را هم به کاربر
 *    می‌گوییم — تحلیل شرایط است، یکی از ورودی‌های تصمیم.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Activity, CalendarDays, Clock3, Gauge as GaugeIcon, Info, RefreshCw, TrendingDown,
  TrendingUp, Minus,
} from 'lucide-react'
import { Shell } from '../components/Layout'
import { Card, Tabs, Empty, Rows } from '../components/UI'
import { get } from '../lib/api'
import { useSeo } from '../lib/seo'
import { clsx, fa, num, faTime, timeAgo } from '../lib/format'
import {
  usd, fngColor, fngLabel, gaugeArc, gaugePoint, linePath, Pt,
  REGIMES, COUNTRIES, IMPORTANCE, signed, altseasonLabel,
} from '../lib/pulse'

type Dash = {
  updatedAt: string | null
  btcUsd: number; totalMcap: number; totalVolume: number; mcapChange: number
  btcD: number; ethD: number; usdtD: number; stableMcap: number
  xauUsd: number
  fng: number; fngLabel: string; fngHistory: { value: number; ts: number }[]
  altseason: number
  altseasonBasis?: string
  regime: string; score: number
  votes: { key: string; label: string; vote: number; detail: string }[]
  upcoming: Ev[]
  stale?: boolean
}

type Ev = {
  id: number; at: string; country: string; title: string
  indicator: string; indicatorFa: string; importance: number; currency: string
  previous: string; forecast: string; actual: string
  surprise: number | null
  impact: { direction: string; text: string }
  assets: string[]; source: string
}

const pct = (v: number, d = 1) => (v >= 0 ? `${num(v, d)}٪` : '—')

const ASSET_FA: Record<string, string> = {
  BTC: 'بیت‌کوین', ETH: 'اتریوم', USDT: 'تتر', XAU: 'طلای جهانی',
  USD: 'دلار', GOLD: 'طلای جهانی',
}

export default function Pulse() {
  const [dash, setDash] = useState<Dash | null>(null)
  const [err, setErr] = useState('')

  useSeo({
    title: 'نبض بازار کریپتو | شاخص ترس و طمع، دامیننس بیت‌کوین و تقویم اقتصادی — ارزینو',
    description:
      'وضعیت لحظه‌ای بازار کریپتو: شاخص ترس و طمع، دامیننس بیت‌کوین و تتر، فصل آلت‌کوین، '
      + 'ارزش کل بازار و تقویم اقتصادی با تحلیل اثر احتمالی بر بیت‌کوین و اتریوم.',
    canonical: '/pulse',
  })

  useEffect(() => {
    let alive = true
    const load = () => get<Dash>('/pulse')
      .then((r) => { if (alive) { setDash(r); setErr('') } })
      .catch((e: any) => { if (alive) setErr(e?.message || 'دریافت داده ناموفق بود') })
    load()
    const id = setInterval(load, 60_000)
    return () => { alive = false; clearInterval(id) }
  }, [])

  return (
    <Shell>
      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-extrabold text-white">
              <Activity className="h-6 w-6 text-brand-400" /> نبض بازار
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              وضعیت کلی بازار کریپتو در یک نگاه — شاخص‌ها، احساسات و رویدادهای پیش رو
            </p>
          </div>
          {dash?.updatedAt && (
            <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
              <RefreshCw className="h-3 w-3" /> به‌روزرسانی {timeAgo(dash.updatedAt)}
            </span>
          )}
        </div>

        {err && !dash && (
          <Card className="mb-6 !border-down/25 !bg-down/[.06] text-center text-sm text-down">{err}</Card>
        )}
        {!dash && !err && <Rows n={6} h="h-24" />}

        {dash && (
          <>
            {dash.stale && (
              <Card className="mb-4 !border-gold/30 !bg-gold/[.06] !py-2.5 text-center text-xs text-gold">
                ارتباط با منابع جهانی موقتاً کند است؛ اعداد این صفحه ممکن است مربوط به
                {' '}{dash.updatedAt ? timeAgo(dash.updatedAt) : 'مدتی پیش'} باشند.
              </Card>
            )}
            <Regime d={dash} />
            <StatGrid d={dash} />
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <FngCard d={dash} />
              <AltseasonCard d={dash} />
            </div>
            <DominanceCard />
            <CalendarCard upcoming={dash.upcoming} />
            <p className="mt-6 flex items-start gap-2 text-[11px] leading-6 text-slate-600">
              <Info className="mt-1 h-3.5 w-3.5 shrink-0" />
              این صفحه تحلیل شرایط بازار است، نه سیگنال یا توصیه خرید و فروش. داده‌ها از
              منابع عمومی جهانی گردآوری و در ارزینو پردازش می‌شوند و ممکن است با تأخیر
              چند دقیقه‌ای همراه باشند.
            </p>
          </>
        )}
      </div>
    </Shell>
  )
}

/* ═══════════════════════════════════ رژیم بازار */

function Regime({ d }: { d: Dash }) {
  const r = REGIMES[d.regime]
  const [open, setOpen] = useState(false)
  if (!r) {
    return (
      <Card className="mb-4 !py-3 text-center text-xs text-slate-500">
        برای تعیین رژیم بازار هنوز داده کافی گردآوری نشده است — چند دقیقه بعد سر بزنید.
      </Card>
    )
  }
  return (
    <Card className="mb-4 !p-0 overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full flex-wrap items-center gap-3 p-4 text-right">
        <span className="rounded-xl px-3 py-1.5 text-sm font-extrabold"
          style={{ background: `${r.color}1f`, color: r.color, border: `1px solid ${r.color}44` }}>
          {r.fa}
        </span>
        <span className="text-xs text-slate-400">{r.desc}</span>
        <span className="ms-auto flex items-center gap-2 text-[11px] text-slate-500">
          {fa(d.votes.length)} نشانه
          <Info className={clsx('h-3.5 w-3.5 transition', open && 'rotate-180')} />
        </span>
      </button>
      {open && (
        <div className="grid gap-2 border-t border-white/[.06] p-4 sm:grid-cols-2 lg:grid-cols-3">
          {d.votes.map((v) => (
            <div key={v.key} className="flex items-center gap-2 rounded-xl border border-white/[.06] bg-white/[.02] px-3 py-2 text-[11px]">
              {v.vote > 0 ? <TrendingUp className="h-3.5 w-3.5 text-up" />
                : v.vote < 0 ? <TrendingDown className="h-3.5 w-3.5 text-down" />
                : <Minus className="h-3.5 w-3.5 text-slate-500" />}
              <span className="text-slate-400">{v.label}</span>
              <span className="num ms-auto text-slate-200" dir="ltr">{v.detail}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

/* ═══════════════════════════════════ کارت‌های شاخص */

function StatGrid({ d }: { d: Dash }) {
  const items: [string, string, string?][] = [
    ['قیمت بیت‌کوین', d.btcUsd > 0 ? `$${num(d.btcUsd)}` : '—'],
    ['طلای جهانی (اونس)', d.xauUsd > 0 ? `$${num(d.xauUsd)}` : '—'],
    ['ارزش کل بازار', usd(d.totalMcap),
      d.mcapChange ? `${d.mcapChange > 0 ? '↑' : '↓'} ${num(Math.abs(d.mcapChange), 1)}٪` : undefined],
    ['حجم معاملات ۲۴س', usd(d.totalVolume)],
    ['دامیننس بیت‌کوین', pct(d.btcD)],
    ['دامیننس اتریوم', pct(d.ethD)],
    ['دامیننس تتر', pct(d.usdtD)],
    ['ارزش استیبل‌کوین‌ها', usd(d.stableMcap)],
    ['ترس و طمع', d.fng >= 0 ? `${fa(d.fng)} — ${d.fngLabel}` : '—'],
  ]
  return (
    <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
      {items.map(([label, value, sub]) => (
        <Card key={label} className="!p-3.5">
          <div className="text-[10px] text-slate-500">{label}</div>
          <div className="num mt-1 text-sm font-bold text-white sm:text-base" dir="ltr">{value}</div>
          {sub && (
            <div className={clsx('num mt-0.5 text-[10px]', sub.startsWith('↑') ? 'text-up' : 'text-down')} dir="ltr">
              {sub}
            </div>
          )}
        </Card>
      ))}
    </div>
  )
}

/* ═══════════════════════════════════ گیج ترس و طمع */

function FngCard({ d }: { d: Dash }) {
  const v = d.fng
  const hist = d.fngHistory || []
  // تاریخچه از منبع «جدید → قدیم» می‌آید
  const ago = (n: number) => hist[n]?.value ?? -1
  const spark = useMemo(() => {
    const pts: Pt[] = hist.slice(0, 31).map((h) => ({ t: h.ts, v: h.value })).reverse()
    return linePath(pts, 260, 48)
  }, [hist])

  const cx = 110, cy = 100, r = 88
  const p = gaugePoint(Math.max(0, v), cx, cy, r - 14)

  return (
    <Card>
      <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
        <GaugeIcon className="h-4 w-4 text-brand-400" /> شاخص ترس و طمع
      </h3>
      {v < 0 ? <Empty text="فعلاً در دسترس نیست" /> : (
        <div className="flex flex-wrap items-center gap-6">
          <svg width="220" height="118" viewBox="0 0 220 118" className="shrink-0">
            {/* پس‌زمینه کمان، پنج بازه رنگی */}
            {[[0, 20, '#FF4D6D'], [20, 40, '#FF8A5C'], [40, 60, '#FFC24D'],
              [60, 80, '#7ED957'], [80, 100, '#00E39A']].map(([a, b, c]) => {
              const s = gaugePoint(Number(a) + 1, cx, cy, r)
              const e = gaugePoint(Number(b) - 1, cx, cy, r)
              return <path key={String(a)}
                d={`M${s.x},${s.y} A${r},${r} 0 0 1 ${e.x},${e.y}`}
                fill="none" stroke={String(c)} strokeOpacity={0.25} strokeWidth={10} strokeLinecap="round" />
            })}
            <path d={gaugeArc(v, cx, cy, r)} fill="none" stroke={fngColor(v)}
              strokeWidth={10} strokeLinecap="round" />
            <line x1={cx} y1={cy} x2={p.x} y2={p.y} stroke={fngColor(v)} strokeWidth={2.5} />
            <circle cx={cx} cy={cy} r={5} fill={fngColor(v)} />
            <text x={cx} y={cy - 26} textAnchor="middle" fontSize="30" fontWeight="800"
              fill={fngColor(v)} className="num">{v}</text>
            <text x={cx} y={cy - 8} textAnchor="middle" fontSize="11" fill="#8B93AD">{fngLabel(v)}</text>
          </svg>
          <div className="min-w-[150px] flex-1 space-y-1.5 text-[11px]">
            {[['دیروز', ago(1)], ['هفته پیش', ago(7)], ['ماه پیش', ago(30)]].map(([l, x]) => (
              <div key={l as string} className="flex items-center justify-between gap-3">
                <span className="text-slate-500">{l}</span>
                {Number(x) >= 0 ? (
                  <span className="num font-semibold" style={{ color: fngColor(Number(x)) }}>
                    {fa(Number(x))} — {fngLabel(Number(x))}
                  </span>
                ) : <span className="text-slate-600">—</span>}
              </div>
            ))}
            {ago(1) >= 0 && (
              <div className="flex items-center justify-between gap-3 border-t border-white/[.06] pt-1.5">
                <span className="text-slate-500">تغییر ۲۴ ساعت</span>
                <span className={clsx('num font-semibold', v - ago(1) >= 0 ? 'text-up' : 'text-down')} dir="ltr">
                  {signed(v - ago(1), 0)}
                </span>
              </div>
            )}
          </div>
          {spark.d && (
            <div className="w-full">
              <div className="mb-1 text-[10px] text-slate-600">۳۰ روز گذشته</div>
              <svg width="100%" height="48" viewBox="0 0 260 48" preserveAspectRatio="none">
                <path d={spark.d} fill="none" stroke={fngColor(v)} strokeWidth={1.6} />
              </svg>
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

/* ═══════════════════════════════════ فصل آلت‌کوین */

function AltseasonCard({ d }: { d: Dash }) {
  const v = d.altseason
  return (
    <Card>
      <h3 className="mb-3 text-sm font-bold text-white">شاخص فصل آلت‌کوین</h3>
      {v < 0 ? <Empty text="فعلاً در دسترس نیست" /> : (
        <>
          <div className="mb-2 flex items-end justify-between">
            <span className="num text-3xl font-extrabold text-white">{fa(Math.round(v))}<span className="text-sm text-slate-500"> / ۱۰۰</span></span>
            <span className="rounded-lg bg-brand-500/15 px-2.5 py-1 text-xs font-bold text-brand-200">
              {altseasonLabel(v)}
            </span>
          </div>
          {/* نوار از «فصل بیت‌کوین» (راست، چون RTL) تا «فصل آلت‌کوین» */}
          <div className="relative h-2.5 w-full overflow-hidden rounded-full"
            style={{ background: 'linear-gradient(to left, #F7931A, #64748B 40%, #64748B 60%, #7C5CFF)' }} dir="ltr">
            <div className="absolute top-1/2 h-4 w-1.5 -translate-y-1/2 rounded-full bg-white shadow"
              style={{ left: `calc(${Math.max(0, Math.min(100, v))}% - 3px)` }} />
          </div>
          <div className="mt-1.5 flex justify-between text-[10px] text-slate-500" dir="ltr">
            <span>فصل بیت‌کوین</span><span>فصل آلت‌کوین</span>
          </div>
          <p className="mt-3 text-[11px] leading-6 text-slate-500">
            {d.altseasonBasis?.startsWith('internal')
              ? `چند درصد از ${fa(Number(d.altseasonBasis.split(':')[1]) || 0)} ارز فهرست‌شده در ارزینو (بدون استیبل‌کوین‌ها) در ~۹۰ روز گذشته از بیت‌کوین بازدهی بهتری داشته‌اند؟ محاسبه از داده معاملاتی خود ارزینو.`
              : 'چند درصد از ۵۰ ارز برتر (بدون استیبل‌کوین‌ها) در ۹۰ روز گذشته از بیت‌کوین بازدهی بهتری داشته‌اند؟'}
            {' '}بالای ۷۵ یعنی فصل آلت‌کوین، زیر ۲۵ یعنی فصل بیت‌کوین.
          </p>
        </>
      )}
    </Card>
  )
}

/* ═══════════════════════════════════ نمودار دامیننس */

const METRICS: { key: 'btcD' | 'ethD' | 'usdtD' | 'mcap' | 'xau'; label: string; color: string; isPct: boolean }[] = [
  { key: 'btcD', label: 'دامیننس بیت‌کوین', color: '#F7931A', isPct: true },
  { key: 'ethD', label: 'دامیننس اتریوم', color: '#627EEA', isPct: true },
  { key: 'usdtD', label: 'دامیننس تتر', color: '#26A17B', isPct: true },
  { key: 'mcap', label: 'ارزش کل بازار', color: '#7C5CFF', isPct: false },
  { key: 'xau', label: 'طلای جهانی', color: '#FFD24D', isPct: false },
]
const SPANS = [{ d: 7, l: '۷ روز' }, { d: 30, l: '۳۰ روز' }, { d: 90, l: '۹۰ روز' }, { d: 365, l: '۱ سال' }]

function DominanceCard() {
  const [metric, setMetric] = useState<typeof METRICS[number]>(METRICS[0])
  const [days, setDays] = useState(7)
  const [data, setData] = useState<{ t: number; [k: string]: number }[] | null>(null)

  useEffect(() => {
    let alive = true
    get<{ points: any[] }>(`/pulse/history?days=${days}`)
      .then((r) => { if (alive) setData(r.points || []) })
      .catch(() => { if (alive) setData([]) })
    return () => { alive = false }
  }, [days])

  const line = useMemo(() => {
    const pts: Pt[] = (data || []).map((p) => ({ t: p.t, v: Number(p[metric.key]) }))
    return linePath(pts, 720, 160)
  }, [data, metric])

  const fmt = (v: number) => metric.isPct ? `${num(v, 2)}٪` : usd(v)

  return (
    <Card className="mt-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-bold text-white">داشبورد دامیننس</h3>
        <div className="ms-auto flex flex-wrap gap-1.5">
          {METRICS.map((m) => (
            <button key={m.key} onClick={() => setMetric(m)}
              className={clsx('rounded-lg px-2.5 py-1 text-[11px] transition',
                metric.key === m.key ? 'ring-1' : 'text-slate-500 hover:text-slate-300')}
              style={metric.key === m.key
                ? { background: `${m.color}1f`, color: m.color, borderColor: m.color } : undefined}>
              {m.label}
            </button>
          ))}
        </div>
        <Tabs size="sm" value={String(days)} onChange={(k) => setDays(Number(k))}
          items={SPANS.map((s) => ({ key: String(s.d), label: s.l }))} />
      </div>

      {data === null ? <Rows n={2} /> : !line.d ? (
        <div className="grid h-40 place-items-center text-center text-xs leading-6 text-slate-500">
          داده تاریخی این بازه هنوز کامل نیست.
          <br />این نمودار از داده‌ای ساخته می‌شود که ارزینو هر ده دقیقه ذخیره می‌کند و روزبه‌روز کامل‌تر می‌شود.
        </div>
      ) : (
        <div dir="ltr">
          <div className="mb-1 flex justify-between text-[10px] text-slate-500">
            <span className="num">{fmt(line.max)}</span>
            <span style={{ color: metric.color }}>{metric.label}</span>
          </div>
          <svg width="100%" height="160" viewBox="0 0 720 160" preserveAspectRatio="none">
            {[0.25, 0.5, 0.75].map((f) => (
              <line key={f} x1={0} x2={720} y1={160 * f} y2={160 * f} stroke="rgba(255,255,255,.05)" />
            ))}
            <path d={`${line.d} L720,160 L0,160 Z`} fill={metric.color} fillOpacity={0.08} />
            <path d={line.d} fill="none" stroke={metric.color} strokeWidth={1.8} />
          </svg>
          <div className="mt-1 text-[10px] text-slate-500"><span className="num">{fmt(line.min)}</span></div>
        </div>
      )}
    </Card>
  )
}

/* ═══════════════════════════════════ تقویم اقتصادی */

function CalendarCard({ upcoming }: { upcoming: Ev[] }) {
  const [range, setRange] = useState('today')
  const [imp, setImp] = useState(0)
  const [country, setCountry] = useState('')
  const [rows, setRows] = useState<Ev[] | null>(null)

  useEffect(() => {
    let alive = true
    setRows(null)
    get<{ events: Ev[] }>(`/pulse/calendar?range=${range}&importance=${imp}${country ? `&country=${country}` : ''}`)
      .then((r) => { if (alive) setRows(r.events || []) })
      .catch(() => { if (alive) setRows([]) })
    return () => { alive = false }
  }, [range, imp, country])

  return (
    <Card className="mt-4 !p-0">
      <div className="flex flex-wrap items-center gap-2 border-b border-white/[.06] p-4">
        <h3 className="flex items-center gap-2 text-sm font-bold text-white">
          <CalendarDays className="h-4 w-4 text-brand-400" /> تقویم اقتصادی
        </h3>
        <Tabs size="sm" className="ms-auto" value={range} onChange={setRange}
          items={[{ key: 'today', label: 'امروز' }, { key: 'tomorrow', label: 'فردا' }, { key: 'week', label: 'این هفته' }]} />
      </div>

      <div className="flex flex-wrap items-center gap-1.5 border-b border-white/[.06] px-4 py-2.5 text-[11px]">
        <span className="text-slate-600">اهمیت</span>
        {[[0, 'همه'], [2, 'متوسط به بالا'], [3, 'فقط زیاد']].map(([v, l]) => (
          <button key={v} onClick={() => setImp(Number(v))}
            className={clsx('rounded-lg px-2 py-1 transition',
              imp === v ? 'bg-brand-500/20 text-brand-200' : 'text-slate-500 hover:text-slate-300')}>
            {l}
          </button>
        ))}
        <span className="ms-3 text-slate-600">کشور</span>
        <button onClick={() => setCountry('')}
          className={clsx('rounded-lg px-2 py-1', !country ? 'bg-brand-500/20 text-brand-200' : 'text-slate-500 hover:text-slate-300')}>
          همه
        </button>
        {Object.entries(COUNTRIES).map(([code, c]) => (
          <button key={code} onClick={() => setCountry(country === code ? '' : code)} title={c.fa}
            className={clsx('rounded-lg px-1.5 py-1 text-sm transition',
              country === code ? 'bg-brand-500/20 ring-1 ring-brand-500/40' : 'opacity-60 hover:opacity-100')}>
            {c.flag}
          </button>
        ))}
      </div>

      {rows === null ? <div className="p-4"><Rows n={3} /></div>
        : rows.length === 0 ? (
          <div className="p-8">
            <Empty text={upcoming.length
              ? 'در این بازه با این فیلترها رویدادی نیست'
              : 'رویدادی ثبت نشده است — تقویم به‌زودی تکمیل می‌شود'} icon={<Clock3 className="h-6 w-6" />} />
          </div>
        ) : (
          <div className="divide-y divide-white/[.04]">
            {rows.map((e) => <EventRow key={e.id} e={e} />)}
          </div>
        )}
    </Card>
  )
}

function EventRow({ e }: { e: Ev }) {
  const c = COUNTRIES[e.country] || { fa: e.country, flag: '🌐' }
  const im = IMPORTANCE[e.importance] || IMPORTANCE[2]
  const released = !!e.actual
  return (
    <div className="p-4">
      <div className="flex flex-wrap items-center gap-2.5">
        <span className="num rounded-lg bg-white/[.04] px-2 py-1 text-[11px] text-slate-300">
          {faTime(e.at)}
        </span>
        <span title={c.fa} className="text-base">{c.flag}</span>
        <span className="text-sm font-semibold text-white">{e.title}</span>
        <span className="flex items-center gap-1 text-[10px]" style={{ color: im.color }}>
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: im.color }} />
          اهمیت {im.fa}
        </span>
        {e.surprise !== null && (
          <span className={clsx('num rounded-md px-1.5 py-0.5 text-[10px] font-bold',
            e.impact.direction === 'up' ? 'bg-up/15 text-up'
              : e.impact.direction === 'down' ? 'bg-down/15 text-down'
              : 'bg-white/[.06] text-slate-300')} dir="ltr">
            Surprise {signed(e.surprise)}
          </span>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-[11px]">
        {[['قبلی', e.previous], ['پیش‌بینی', e.forecast], ['واقعی', e.actual]].map(([l, v]) => (
          <span key={l} className="flex items-center gap-1.5">
            <span className="text-slate-600">{l}</span>
            <span className={clsx('num', l === 'واقعی' && v ? 'font-bold text-white' : 'text-slate-300')} dir="ltr">
              {v || '—'}
            </span>
          </span>
        ))}
        {e.assets.length > 0 && (
          <span className="flex items-center gap-1">
            <span className="text-slate-600">دارایی‌های حساس</span>
            {e.assets.map((a) => (
              <span key={a} title={ASSET_FA[a] || a}
                className="num rounded bg-white/[.05] px-1.5 py-0.5 text-[10px] text-slate-300" dir="ltr">{a}</span>
            ))}
          </span>
        )}
      </div>

      {e.impact.text && (
        <p className={clsx('mt-2 rounded-xl px-3 py-2 text-[11px] leading-6',
          released && e.impact.direction === 'up' ? 'bg-up/[.07] text-up/90'
            : released && e.impact.direction === 'down' ? 'bg-down/[.07] text-down/90'
            : 'bg-white/[.03] text-slate-400')}>
          {e.impact.text}
        </p>
      )}
    </div>
  )
}
